/* ============================================================
 * <lion-fbt-bundle> — Frequently bought together.
 *
 * Lazy-fetches the FBT bundle section via Shopify's recommendations
 * endpoint with intent=complementary. Wires up checkbox total and
 * a bundle Add-to-cart that POSTs all selected variants to /cart/add.js.
 *
 * Attributes:
 *   data-url            Full recommendations URL (built server-side).
 *   data-product-id     Source product id.
 *
 * Subscribes to:
 *   - variantChange (updates source variant id used at ATC time)
 *
 * Publishes:
 *   - cartUpdate (after successful multi-add so cart drawer refreshes)
 * ============================================================ */

class FbtBundle extends HTMLElement {
  constructor() {
    super();
    this.url = this.dataset.url;
    this.loaded = false;

    // Lazy load when the element scrolls near the viewport.
    this.io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !this.loaded) {
          this.loaded = true;
          this.load();
          this.io.disconnect();
          this.io = null;
        }
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.io.observe(this);

    // Pending variant update if it arrives before load() completes.
    this.pendingVariant = null;

    // Keep source variant id in sync with the variant picker.
    this.variantChangeUnsub = window.subscribe?.(
      window.PUB_SUB_EVENTS?.variantChange,
      ({ variant }) => {
        if (!variant) return;
        if (!this.loaded) {
          this.pendingVariant = variant;
          return;
        }
        this.applyVariantToSelf(variant);
      }
    );
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.io = null;
    this.variantChangeUnsub?.();
  }

  applyVariantToSelf(variant) {
    const selfItem = this.querySelector('[data-fbt-item][data-self]');
    if (!selfItem) return;
    selfItem.dataset.variantId = String(variant.id);
    selfItem.dataset.price = String(variant.price);
    this.recomputeTotal();
  }

  async load() {
    try {
      const res = await fetch(this.url, { headers: { 'Accept': 'text/html' } });
      if (!res.ok) throw new Error(res.statusText);
      const html = await res.text();
      // Parse and graft only the .fbt-bundle subtree — defends against any
      // accidental layout HTML in the section response.
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const root = doc.querySelector('[data-fbt-root]');
      if (!root) {
        this.hidden = true;
        return;
      }
      // Mark the self item so future variant updates can find it.
      const selfItem = root.querySelector('.fbt-bundle__item--self');
      if (selfItem) selfItem.setAttribute('data-self', '1');
      this.replaceChildren(...root.childNodes);

      this.wireUp();
      // If a variant change arrived while we were fetching, apply it now.
      if (this.pendingVariant) {
        this.applyVariantToSelf(this.pendingVariant);
        this.pendingVariant = null;
      } else {
        this.recomputeTotal();
      }
    } catch (e) {
      console.warn('[lion-fbt-bundle] load failed:', e);
      this.hidden = true;
    }
  }

  wireUp() {
    this.querySelectorAll('[data-fbt-toggle]').forEach((cb) => {
      cb.addEventListener('change', () => this.recomputeTotal());
    });
    const btn = this.querySelector('[data-fbt-add]');
    btn?.addEventListener('click', () => this.addBundleToCart(btn));
  }

  getSelectedItems() {
    const items = [];
    // Source item is always added (its checkbox is checked + disabled).
    const selfItem = this.querySelector('[data-fbt-item][data-self]');
    if (selfItem) {
      items.push({ id: Number(selfItem.dataset.variantId), quantity: 1 });
    }
    this.querySelectorAll('[data-fbt-item]:not([data-self])').forEach((item) => {
      const cb = item.querySelector('[data-fbt-toggle]');
      if (cb?.checked && !cb.disabled) {
        const vid = Number(item.dataset.variantId);
        if (Number.isFinite(vid) && vid > 0) items.push({ id: vid, quantity: 1 });
      }
    });
    return items;
  }

  recomputeTotal() {
    const totalEl = this.querySelector('[data-fbt-total]');
    if (!totalEl) return;
    let totalCents = 0;
    const selfItem = this.querySelector('[data-fbt-item][data-self]');
    if (selfItem) totalCents += parseInt(selfItem.dataset.price, 10) || 0;
    this.querySelectorAll('[data-fbt-item]:not([data-self])').forEach((item) => {
      const cb = item.querySelector('[data-fbt-toggle]');
      if (cb?.checked && !cb.disabled) totalCents += parseInt(item.dataset.price, 10) || 0;
    });
    totalEl.textContent = this.formatMoney(totalCents);
  }

  formatMoney(cents) {
    const code = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  async addBundleToCart(button) {
    const items = this.getSelectedItems();
    if (!items.length) return;

    const err = this.querySelector('[data-fbt-error]');
    if (err) { err.hidden = true; err.textContent = ''; }

    const label = this.querySelector('[data-fbt-add-label]');
    const original = label?.textContent;
    button.disabled = true;
    if (label) label.textContent = window.lionStrings?.adding || 'Adding…';

    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data?.description || data?.message || (window.lionStrings?.cartError || 'Failed to add bundle.');
        throw new Error(msg);
      }
      // Cart drawer subscribes to cartUpdate and refreshes itself + the
      // header badge via the Section Rendering API.
      window.publish?.(window.PUB_SUB_EVENTS?.cartUpdate, { source: 'lion-fbt-bundle' });
    } catch (e) {
      console.warn('[lion-fbt-bundle] add failed:', e);
      if (err) {
        err.textContent = e.message || 'Could not add bundle';
        err.hidden = false;
      }
    } finally {
      button.disabled = false;
      if (label && original) label.textContent = original;
    }
  }
}

if (!customElements.get('lion-fbt-bundle')) {
  customElements.define('lion-fbt-bundle', FbtBundle);
}
